// VibeScreen — vibe sliders + map + distance. The whole canvas tint shifts
// as you drag, so the act of setting your mood feels physical.
// DiscoverScreen — Tinder-style draggable card stack, save-on-right / pass-on-left.

import React from 'react';
import { LOCATIONS } from './data.js';
import { Icon, Constellation, LocationMap, HeroArt, Switch } from './components.jsx';
import { matchScore } from './lib/match.js';
import { distLabel } from './lib/geo.js';
import { useHomeBase } from './context.js';

// A preset is the same fixed values every time it's tapped. The backend picks
// which place categories to fetch from vibeToApiPrefs(vibe) rounded to a 0-10
// scale (see lib/prefs.js), so the jitter has to be big enough to reliably
// flip that rounded value by a point, or picking the same preset twice in a
// row still pulls the exact same category mix / card stack from the server.
const VIBE_JITTER = 7;
function jitterVibe(v) {
  return Object.fromEntries(
    Object.entries(v).map(([key, val]) => [
      key,
      Math.min(100, Math.max(0, val + Math.round((Math.random() * 2 - 1) * VIBE_JITTER))),
    ])
  );
}

export function VibeScreen({ vibe, setVibe, prefs, setPrefs, onStart, base, locationId, activities, onChangeLocation, customGPS, onMapClick, onClearCustom }) {
  const bgHue = 18 + ((vibe.physical ?? 50) / 100) * 24;
  const bgLight = 0.86 - ((100 - (vibe.outdoor ?? 50)) / 100) * 0.10;
  const bgChroma = 0.10 + ((vibe.social ?? 50) / 100) * 0.06;
  const bgEnd = `oklch(${bgLight} ${bgChroma} ${bgHue})`;
  const bgStart = `oklch(${Math.min(0.95, bgLight + 0.06)} ${bgChroma * 0.6} ${bgHue + 12})`;

  return (
    <div className="screen" style={{
      background: `linear-gradient(180deg, ${bgStart} 0%, ${bgEnd} 100%)`,
      transition: 'background 0.4s ease',
    }}>
      <div className="hdr">
        <div>
          <div className="eyebrow">Tonight in {base?.name || LOCATIONS.find(l => l.id === locationId)?.name || 'New York City'}</div>
          <h1>What's the <span className="serif" style={{ color: 'var(--terracotta)' }}>vibe</span>?</h1>
        </div>
      </div>

      <div className="scroll" style={{ padding: '20px 22px 24px' }}>
        <p className="soft" style={{ fontSize: 15, lineHeight: 1.45, margin: '0 0 18px', maxWidth: 320 }}>
          Dial in your mood and range.<br/>We'll find matches.
        </p>

        <LocationMap base={base} locationId={locationId}
                     distance={prefs.distance}
                     setDistance={v => setPrefs({ ...prefs, distance: v })}
                     activities={activities} onChangeLocation={onChangeLocation}
                     customGPS={customGPS} onMapClick={onMapClick} onClearCustom={onClearCustom} />

        <div className="row" style={{ gap: 8, margin: '16px 0 20px', flexWrap: 'wrap' }}>
          <span className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600, width: '100%', marginBottom: 4 }}>Or grab a preset</span>
          {[
            { id: 'rainy',     label: 'rainy Sunday',      v: { physical: 15, social: 25, outdoor: 10, newPeople: 10, creative: 65, boozy: 15, eating: 70, expensive: 15, nsfw: 0,  fun: 40 } },
            { id: 'date',      label: 'first date',        v: { physical: 35, social: 65, outdoor: 50, newPeople: 70, creative: 40, boozy: 45, eating: 65, expensive: 60, nsfw: 20, fun: 70 } },
            { id: 'rally',     label: 'rally the crew',    v: { physical: 55, social: 90, outdoor: 50, newPeople: 30, creative: 25, boozy: 75, eating: 65, expensive: 45, nsfw: 30, fun: 90 } },
            { id: 'solo',      label: 'solo recharge',     v: { physical: 25, social: 10, outdoor: 45, newPeople: 5,  creative: 60, boozy: 5,  eating: 30, expensive: 15, nsfw: 0,  fun: 40 } },
            { id: 'walk',      label: 'long walk',         v: { physical: 80, social: 30, outdoor: 95, newPeople: 15, creative: 20, boozy: 5,  eating: 15, expensive: 5,  nsfw: 0,  fun: 55 } },
            { id: 'brunch',    label: 'brunch with friends', v: { physical: 20, social: 75, outdoor: 30, newPeople: 20, creative: 15, boozy: 40, eating: 85, expensive: 45, nsfw: 0,  fun: 65 } },
            { id: 'night-out', label: 'big night out',     v: { physical: 50, social: 95, outdoor: 15, newPeople: 60, creative: 20, boozy: 85, eating: 40, expensive: 70, nsfw: 40, fun: 95 } },
          ].map(p => (
            <button key={p.id} className="chip" style={{ height: 32, padding: '0 14px', fontSize: 13, cursor: 'pointer' }}
                    onClick={() => setVibe(jitterVibe(p.v))}>
              {p.label}
            </button>
          ))}
        </div>

        <div style={{ marginBottom: 20 }}>
          <span className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600, display: 'block', marginBottom: 10 }}>Options</span>
          <Switch checked={prefs.showRevisits}
                  onChange={v => setPrefs(p => ({ ...p, showRevisits: v }))}
                  label="Include places I've seen before"/>
        </div>

        {[
          { key: 'physical',  label: 'Physical',    left: 'sedated',        right: 'active',
            hint: v => v < 33 ? 'take it easy' : v < 67 ? 'moderate' : 'get moving' },
          { key: 'social',    label: 'Social',      left: 'solo',           right: 'crowd',
            hint: v => v < 33 ? 'just me' : v < 67 ? 'small group' : 'the more the merrier' },
          { key: 'outdoor',   label: 'Setting',     left: 'indoors',        right: 'outdoors',
            hint: v => v < 33 ? 'cozy inside' : v < 67 ? 'either works' : 'fresh air' },
          { key: 'newPeople', label: 'People',      left: 'familiar faces', right: 'meet people',
            hint: v => v < 33 ? 'stick to the crew' : v < 67 ? 'open to it' : 'love meeting people' },
          { key: 'creative',  label: 'Creative',    left: 'watching',       right: 'making',
            hint: v => v < 33 ? 'consuming content' : v < 67 ? 'bit of both' : 'hands on' },
          { key: 'boozy',     label: 'Drinks',      left: 'sober',          right: 'boozy',
            hint: v => v < 33 ? 'no thanks' : v < 67 ? 'maybe one or two' : 'bar night' },
          { key: 'eating',    label: 'Food',        left: 'not hungry',     right: 'starving',
            hint: v => v < 33 ? 'just had something' : v < 67 ? 'could eat' : 'very hungry' },
          { key: 'expensive', label: 'Budget',      left: 'free / cheap',   right: 'splurge',
            hint: v => v < 33 ? 'keep it cheap' : v < 67 ? 'moderate' : 'treat yourself' },
        ].map(({ key, label, left, right, hint }) => (
          <VibeSlider key={key} label={label} value={vibe[key] ?? 50}
                      hint={hint(vibe[key] ?? 50)}
                      onChange={v => setVibe({ ...vibe, [key]: v })}
                      left={left} right={right} />
        ))}
      </div>

      <div style={{ padding: '8px 22px 16px', position: 'relative', zIndex: 5 }}>
        <button className="btn coral full" onClick={onStart}>
          Find things to do
          <span style={{ opacity: 0.7, marginLeft: 4 }}>→</span>
        </button>
      </div>
    </div>
  );
}

function VibeSlider({ label, value, onChange, hint, left, right }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div className="row between" style={{ marginBottom: 6 }}>
        <span className="upper" style={{ fontSize: 10, color: 'var(--ink-mute)', fontWeight: 600 }}>{label}</span>
        <span className="serif" style={{ fontSize: 18, color: 'var(--terracotta)' }}>{hint}</span>
      </div>
      <div style={{ position: 'relative' }}>
        <input type="range" min={0} max={100} value={value}
               aria-label={label}
               onChange={e => onChange(Number(e.target.value))}
               style={{
                 width: '100%', accentColor: 'var(--coral)',
                 height: 6, '--range-frac': value / 100,
               }} />
        <div className="row between" style={{ marginTop: 2, fontSize: 11, color: 'var(--ink-mute)' }}>
          <span>{left}</span><span>{right}</span>
        </div>
      </div>
    </div>
  );
}

// ── Discover / swipe deck ──────────────────────────────────────────────────
function CardSkeleton({ depth = 0 }) {
  return (
    <div style={{
      position: 'absolute', inset: 0,
      background: 'var(--paper)',
      borderRadius: 24,
      overflow: 'hidden',
      boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      opacity: 1 - depth * 0.15,
      transform: `translateY(${depth * 10}px) scale(${1 - depth * 0.04})`,
      zIndex: 3 - depth,
    }}>
      <div style={{
        height: '56%',
        background: 'linear-gradient(135deg, var(--bg-tint) 0%, var(--bg) 100%)',
        animation: 'larkle-shimmer 1.4s ease-in-out infinite',
      }}/>
      <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[['60%', 0], ['40%', 0.15], ['88%', 0.3], ['72%', 0.45]].map(([w, delay], i) => (
          <div key={i} style={{
            height: i === 0 ? 22 : 13,
            width: w,
            borderRadius: 7,
            background: 'var(--bg)',
            animation: `larkle-shimmer 1.4s ease-in-out ${delay}s infinite`,
          }}/>
        ))}
      </div>
    </div>
  );
}

export function DiscoverScreen({ vibe, deck, onSwipe, onOpen, onShare, locationName, onTweakVibe, loading, error, onRetry }) {
  const [drag, setDrag] = React.useState({ id: null, dx: 0, dy: 0 });
  const [exiting, setExiting] = React.useState(null); // { id, dir }
  const startRef = React.useRef(null);

  const visible = deck.slice(0, 3); // top 3 cards stacked
  const top = visible[0];

  const onPointerDown = (e, id) => {
    if (exiting) return;
    if (id !== top?.id) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    startRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    setDrag({ id, dx: 0, dy: 0 });
  };
  const onPointerMove = (e) => {
    if (!startRef.current || drag.id !== top?.id) return;
    setDrag({ id: drag.id, dx: e.clientX - startRef.current.x, dy: e.clientY - startRef.current.y });
  };
  const onPointerUp = () => {
    if (!startRef.current) return;
    const { id, dx, dy } = drag;
    const dt = Date.now() - startRef.current.t;
    const fast = Math.abs(dx) / Math.max(80, dt) > 0.6;
    if (Math.abs(dx) > 120 || fast) {
      const dir = dx > 0 ? 'right' : 'left';
      setExiting({ id: drag.id, dir, startDx: dx });
      setTimeout(() => {
        onSwipe(drag.id, dir);
        setExiting(null);
        setDrag({ id: null, dx: 0, dy: 0 });
      }, 240);
    } else {
      // Barely moved — treat as a tap on the card, not an aborted swipe.
      const isTap = Math.abs(dx) < 6 && Math.abs(dy) < 6;
      setDrag({ id: null, dx: 0, dy: 0 });
      if (isTap && id) {
        // The tap's trailing click event hit-tests after the detail sheet
        // mounts — while the sheet is still sliding in from offscreen it
        // lands on the scrim and instantly closes what the tap just opened.
        // Swallow that one click; the timeout covers browsers that skip it.
        const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
        window.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 400);
        onOpen(id);
      }
    }
    startRef.current = null;
  };

  const fireSwipe = (dir) => {
    if (!top || exiting) return;
    setExiting({ id: top.id, dir, startDx: dir === 'right' ? 60 : -60 });
    setTimeout(() => {
      onSwipe(top.id, dir);
      setExiting(null);
      setDrag({ id: null, dx: 0, dy: 0 });
    }, 240);
  };

  // Error — nothing loaded and the fetch failed
  if (!top && !exiting && !loading && error) {
    return (
      <div className="screen" style={{ background: 'var(--bg)' }}>
        <div className="scroll" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', padding: 32, textAlign: 'center', gap: 14 }}>
          <div className="serif" style={{ fontSize: 32, color: 'var(--terracotta)', lineHeight: 1.05 }}>
            {error === 'offline' ? "You're offline." : "Couldn't load places."}
          </div>
          <p className="soft" style={{ maxWidth: 280, fontSize: 14 }}>
            {error === 'offline'
              ? 'Check your connection and try again.'
              : 'Something went wrong talking to the server.'}
          </p>
          <button className="btn coral" style={{ height: 44, padding: '0 28px' }} onClick={onRetry}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Empty — no cards and nothing incoming
  if (!top && !exiting && !loading) {
    return (
      <div className="screen" style={{ background: 'var(--bg)' }}>
        <div className="scroll" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', padding: 32, textAlign: 'center', gap: 14 }}>
          <div className="serif" style={{ fontSize: 36, color: 'var(--terracotta)', lineHeight: 1.05 }}>
            That's the whole<br/>neighborhood for now.
          </div>
          <p className="soft" style={{ maxWidth: 280, fontSize: 14 }}>
            Widen your radius or switch neighborhoods on the Vibe tab to see more.
          </p>
          <button className="btn ghost" style={{ height: 44, padding: '0 24px' }} onClick={onTweakVibe}>
            Adjust the vibe
          </button>
        </div>
      </div>
    );
  }

  // How many skeleton slots to show behind the real cards
  const skeletonCount = loading ? Math.max(0, 3 - visible.length) : 0;

  return (
    <div className="screen" style={{ background: 'var(--bg)' }}>
      <div style={{ padding: '52px 22px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="col" style={{ gap: 2 }}>
          <span className="eyebrow">{locationName}</span>
          <span className="serif" style={{ fontSize: 22, color: 'var(--ink)', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
            tonight, <span style={{ color: 'var(--terracotta)' }}>maybe…</span>
          </span>
        </div>
        <button className="iconbtn" aria-label="Adjust vibe" onClick={onTweakVibe} style={{ width: 36, height: 36 }}>
          <span style={{ width: 16, height: 16, color: 'var(--ink)' }}>{Icon.compass}</span>
        </button>
      </div>

      <div style={{ flex: 1, position: 'relative', padding: '6px 18px 4px', minHeight: 0 }}>
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          {/* skeleton cards sit behind real cards while next page loads */}
          {Array.from({ length: skeletonCount }).map((_, i) => {
            const stackIdx = visible.length + i;
            return (
              <div key={`skel-${i}`} style={{
                position: 'absolute', inset: 0,
                transform: `translateY(${stackIdx * 10}px) scale(${1 - stackIdx * 0.04})`,
                zIndex: 10 - stackIdx,
              }}>
                <CardSkeleton depth={0}/>
              </div>
            );
          })}
          {/* real cards */}
          {visible.slice().reverse().map((a, idxFromBack) => {
            const stackIdx = visible.length - 1 - idxFromBack; // 0 = top
            const isTop = stackIdx === 0;
            const isExiting = exiting && exiting.id === a.id;
            const dx = isTop ? (isExiting ? (exiting.dir === 'right' ? 600 : -600) : drag.dx) : 0;
            const dy = isTop ? (isExiting ? 60 : drag.dy * 0.4) : 0;
            const rot = dx * 0.06;
            const scale = 1 - stackIdx * 0.04;
            const ty = stackIdx * 10;
            return (
              <div key={a.id}
                   onPointerDown={isTop ? (e) => onPointerDown(e, a.id) : undefined}
                   onPointerMove={isTop ? onPointerMove : undefined}
                   onPointerUp={isTop ? onPointerUp : undefined}
                   onPointerCancel={isTop ? onPointerUp : undefined}
                   style={{
                     position: 'absolute', inset: 0,
                     transform: `translate(${dx}px, ${dy + ty}px) rotate(${rot}deg) scale(${scale})`,
                     transition: isExiting || !startRef.current ? 'transform 0.24s cubic-bezier(.2,.7,.2,1)' : 'none',
                     zIndex: 10 - stackIdx,
                     pointerEvents: isTop ? 'auto' : 'none',
                     touchAction: 'none',
                     cursor: isTop ? (drag.id ? 'grabbing' : 'grab') : 'default',
                   }}>
                <SwipeCard activity={a} vibe={vibe} dx={isTop ? dx : 0}/>
              </div>
            );
          })}
        </div>
      </div>

      {/* action row */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 18, padding: '14px 22px 4px', flexShrink: 0 }}>
        <button className="iconbtn" style={{ width: 56, height: 56, background: 'var(--paper)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                onClick={() => fireSwipe('left')} aria-label="Pass">
          <span style={{ color: 'var(--red)', width: 22, height: 22 }}>{Icon.x}</span>
        </button>
        <button className="iconbtn" style={{ width: 48, height: 48, background: 'var(--paper)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                onClick={() => top && onShare(top.id)} aria-label="Share">
          <span style={{ color: 'var(--ink)', width: 20, height: 20 }}>{Icon.share}</span>
        </button>
        <button className="iconbtn" style={{ width: 48, height: 48, background: 'var(--paper)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                onClick={() => top && onOpen(top.id)} aria-label="Details">
          <span style={{ color: 'var(--ink)', width: 20, height: 20 }}>{Icon.info}</span>
        </button>
        <button className="iconbtn" style={{ width: 56, height: 56, background: 'var(--paper)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                onClick={() => fireSwipe('right')} aria-label="Save">
          <span style={{ color: 'var(--green)', width: 24, height: 24 }}>{Icon.bookmark}</span>
        </button>
      </div>
    </div>
  );
}

// Individual card. dx drives the overlay reveal so user sees confirmation
// (orange SAVE on right, gray PASS on left) before they commit.
function SwipeCard({ activity, vibe, dx }) {
  const base = useHomeBase();
  const score = matchScore(vibe, activity);
  const rightOpacity = Math.min(1, Math.max(0, dx / 100));
  const leftOpacity = Math.min(1, Math.max(0, -dx / 100));
  return (
    <div style={{
      position: 'relative',
      width: '100%', height: '100%',
      borderRadius: 28,
      background: 'var(--paper)',
      boxShadow: '0 6px 24px rgba(0,0,0,0.12), 0 1px 0 rgba(0,0,0,0.04) inset',
      overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <HeroArt activity={activity} height="100%" rounded={0}/>
        {/* match chip */}
        <div style={{ position: 'absolute', top: 14, left: 14, display: 'flex', gap: 6, alignItems: 'center' }}>
          <div className="chip dark" style={{ height: 28, fontSize: 12, padding: '0 12px', fontWeight: 600 }}>
            <span className="serif" style={{ fontStyle: 'italic', fontSize: 16, marginRight: 4 }}>{score}</span>
            % match
          </div>
        </div>
        {/* constellation top-right */}
        <div style={{ position: 'absolute', top: 8, right: 8 }}>
          <Constellation user={vibe} activity={activity} size={68} light/>
        </div>
        {/* gradient bottom */}
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, height: '50%',
          background: 'linear-gradient(to top, rgba(20,10,8,0.85) 0%, rgba(20,10,8,0.35) 60%, transparent 100%)',
        }}/>
        {/* bottom text */}
        <div style={{ position: 'absolute', left: 18, right: 18, bottom: 16, color: 'var(--paper)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.85, fontWeight: 600 }}>
            <span>{activity.kind}</span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span>{distLabel(activity, base)}</span>
          </div>
          <h2 className="serif" style={{
            fontSize: 28, lineHeight: 1.05, margin: 0,
            color: 'var(--paper)', fontWeight: 400, textWrap: 'pretty',
          }}>{activity.name}</h2>
          {/* blurb falls back to the name for un-enriched places — repeating
              the title right under itself reads broken, so skip it */}
          {activity.blurb && activity.blurb.trim() !== activity.name.trim() && (
            <p style={{ margin: '8px 0 10px', fontSize: 13.5, lineHeight: 1.4, opacity: 0.92, textWrap: 'pretty' }}>
              {activity.blurb}
            </p>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {activity.tags.slice(0, 3).map(t => (
              <span key={t} className="chip dark" style={{ fontSize: 10.5, height: 22, padding: '0 9px' }}>{t}</span>
            ))}
          </div>
          {/* why-this-matches */}
          <div style={{
            marginTop: 12, padding: '10px 12px', borderRadius: 12,
            background: 'rgba(255,255,255,0.10)',
            backdropFilter: 'blur(6px)',
            border: '0.5px solid rgba(255,255,255,0.18)',
          }}>
            <div className="upper" style={{ fontSize: 9, opacity: 0.8, fontWeight: 600, marginBottom: 3 }}>Why this matches</div>
            <div style={{ fontSize: 12, lineHeight: 1.35, textWrap: 'pretty' }}>{activity.why}</div>
          </div>
        </div>

        {/* SAVE / PASS overlays */}
        <div style={{
          position: 'absolute', top: 28, left: 28,
          padding: '8px 16px', border: '3px solid var(--green)', color: 'var(--green)',
          borderRadius: 12, fontWeight: 700, fontSize: 22, letterSpacing: 0.06,
          transform: `rotate(-12deg) scale(${0.8 + rightOpacity * 0.2})`,
          opacity: rightOpacity, background: 'rgba(255,255,255,0.85)',
          pointerEvents: 'none',
        }}>SAVE</div>
        <div style={{
          position: 'absolute', top: 28, right: 28,
          padding: '8px 16px', border: '3px solid var(--red)', color: 'var(--red)',
          borderRadius: 12, fontWeight: 700, fontSize: 22, letterSpacing: 0.06,
          transform: `rotate(12deg) scale(${0.8 + leftOpacity * 0.2})`,
          opacity: leftOpacity, background: 'rgba(255,255,255,0.85)',
          pointerEvents: 'none',
        }}>PASS</div>
      </div>
    </div>
  );
}
